import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './ui/tokens.css';
import { App } from './app/App';
import { SessionProvider } from './app/session-context';
import { ThemeProvider } from './app/theme';
import { registerServiceWorker } from './features/notifications/push';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Retrying a 401/403 just burns the rotating refresh token.
        const status = (error as { statusCode?: number }).statusCode;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root bulunamadı');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SessionProvider>
          <App />
        </SessionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);

/*
 * Registered after the first render, and never awaited.
 *
 * The worker is what makes the app installable and what receives the pushes,
 * but neither is worth delaying first paint for — and a registration that
 * fails (an old browser, a private window, a proxy serving sw.js as HTML) must
 * leave the app exactly as capable as it was before push existed.
 *
 * Dev is excluded: Vite serves modules the worker's navigation fallback has no
 * business intercepting, and a worker cached from a previous run is a
 * memorable afternoon.
 */
if (import.meta.env.PROD) {
  void registerServiceWorker();
}
